// Panels and bars over the remote view: permission requests, file chooser,
// downloads, JS dialogs, toasts and the new tab page.
import { useEffect, useRef, useState } from 'react';
import { Download as DownloadIcon, Globe, Search, X } from 'lucide-react';
import { t, tPerm } from '../core/i18n';
import type { PermissionRequest } from '../core/permissions';
import type { Download } from '../core/protocol';
import { hostOf, normalizeInput } from '../core/url';

const joinList = (items: string[]) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} ${t('and')} ${items[items.length - 1]}`);

export function PermissionPrompt({ req, onDecide }: { req: PermissionRequest; onDecide: (allow: boolean, remember: boolean) => Promise<string | void> }) {
  const rememberable = !req.kinds.includes('display-capture');
  const [remember, setRemember] = useState(rememberable);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const decide = async (allow: boolean) => {
    setBusy(true);
    const err = await onDecide(allow, remember);
    if (err) {
      setError(err);
      setBusy(false);
    }
  };
  return (
    <div className="panel" role="dialog" aria-live="polite">
      <p>
        <strong>{hostOf(req.origin)}</strong> {t('wantsTo')} {joinList(req.kinds.map(tPerm))}.
      </p>
      {error && <div className="error">{error}</div>}
      {rememberable && (
        <label className="check">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          {t('remember')}
        </label>
      )}
      <div className="actions">
        <button className="btn" disabled={busy} onClick={() => decide(false)}>
          {t('block')}
        </button>
        <button className="btn primary" disabled={busy} onClick={() => decide(true)} autoFocus>
          {t('allow')}
        </button>
      </div>
    </div>
  );
}

export function FileChooserPanel(p: { origin: string; multiple: boolean; accept: string; progress: number | null; onFiles: (f: File[]) => void; onCancel: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // The click on the remote page usually still counts as user activation.
    try {
      input.current?.click();
    } catch {
      /* the button below works anyway */
    }
  }, []);
  return (
    <div className="panel" role="dialog">
      <p>
        <strong>{hostOf(p.origin) || 'Website'}</strong> {t('fileWanted')}.
      </p>
      {p.progress !== null ? (
        <>
          <div className="bar-progress">
            <div style={{ width: `${Math.round(p.progress * 100)}%` }} />
          </div>
          <p className="muted" style={{ marginTop: 6, fontSize: 12 }}>
            {t('uploading')}
          </p>
        </>
      ) : (
        <div className="actions">
          <button className="btn" onClick={p.onCancel}>
            {t('cancel')}
          </button>
          <button className="btn primary" onClick={() => input.current?.click()}>
            {p.multiple ? t('chooseFiles') : t('chooseFile')}
          </button>
        </div>
      )}
      <input
        ref={input}
        type="file"
        hidden
        multiple={p.multiple}
        accept={p.accept || undefined}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) p.onFiles(files);
        }}
      />
    </div>
  );
}

const fmtBytes = (n: number) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);

export function DownloadsBar({ items, onSave, onClear }: { items: Download[]; onSave: (d: Download) => void; onClear: () => void }) {
  if (!items.length) return null;
  return (
    <div className="downloads">
      <div className="downloads-list">
        {items.map((d) => (
          <button key={d.guid} className="download" disabled={d.state !== 'completed'} onClick={() => onSave(d)} title={d.filename}>
            <DownloadIcon size={15} />
            <span className="download-info">
              <span className="download-name">{d.filename}</span>
              <span className="download-meta">
                {d.state === 'completed'
                  ? fmtBytes(d.total || d.received)
                  : d.state === 'canceled'
                    ? t('cancel')
                    : d.total
                      ? `${fmtBytes(d.received)} / ${fmtBytes(d.total)}`
                      : fmtBytes(d.received)}
              </span>
            </span>
          </button>
        ))}
      </div>
      <button className="icon-btn" aria-label={t('clearDownloads')} title={t('clearDownloads')} onClick={onClear}>
        <X size={15} />
      </button>
    </div>
  );
}

export interface DialogReq {
  id: number;
  type: 'alert' | 'confirm' | 'prompt' | 'beforeunload';
  message: string;
  defaultPrompt: string;
  origin: string;
}

export function JsDialog({ d, onClose }: { d: DialogReq; onClose: (accept: boolean, text?: string) => void }) {
  const [text, setText] = useState(d.defaultPrompt);
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog"
        role="alertdialog"
        onSubmit={(e) => {
          e.preventDefault();
          onClose(true, text);
        }}
      >
        <h2>
          {hostOf(d.origin) || 'Website'} {t('says')}
        </h2>
        <p>{d.type === 'beforeunload' ? t('leavePage') : d.message}</p>
        {d.type === 'prompt' && <input className="input" autoFocus value={text} onChange={(e) => setText(e.target.value)} />}
        <div className="actions">
          {d.type !== 'alert' && (
            <button type="button" className="btn" onClick={() => onClose(false)}>
              {t('cancel')}
            </button>
          )}
          <button type="submit" className="btn primary" autoFocus={d.type !== 'prompt'}>
            {t('ok')}
          </button>
        </div>
      </form>
    </div>
  );
}

export interface RecentSite {
  url: string;
  title: string;
  favicon?: string;
}

export function NewTabPage({ recent, onNavigate }: { recent: RecentSite[]; onNavigate: (url: string) => void }) {
  const [q, setQ] = useState('');
  return (
    <div className="newtab">
      <div className="newtab-inner">
        <form
          className="newtab-search"
          onSubmit={(e) => {
            e.preventDefault();
            if (q.trim()) onNavigate(normalizeInput(q));
          }}
        >
          <Search size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('searchPlaceholder')} autoCapitalize="off" spellCheck={false} enterKeyHint="go" aria-label={t('searchPlaceholder')} />
        </form>
        {recent.length > 0 && (
          <div className="recent">
            <h2>{t('recent')}</h2>
            <ul>
              {recent.map((r) => (
                <li key={r.url}>
                  <button onClick={() => onNavigate(r.url)}>
                    {r.favicon ? <img src={r.favicon} alt="" /> : <Globe className="r-icon" size={16} />}
                    <span className="r-title">{r.title || r.url}</span>
                    <span className="r-host">{hostOf(new URL(r.url).origin).replace(/^www\./, '')}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export function Toast({ text, onClick, onClose }: { text: string; onClick?: () => void; onClose: () => void }) {
  useEffect(() => {
    const id = setTimeout(onClose, 6000);
    return () => clearTimeout(id);
  }, [text, onClose]);
  return (
    <div className={'toast' + (onClick ? ' clickable' : '')} role="status" onClick={onClick}>
      {text}
    </div>
  );
}
